import {
  TrendingUp,
  TrendingDown,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import type { Loan } from '@/types'
import { amountUnitOf, toMillions } from '@/pages/liveModuleData'

interface Props {
  deals: Loan[]
  live?: boolean
}

const fmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`
/** A figure nothing recorded is "—", never $0.0M or 0.0% (MOD-24). */
const money = (v: number | null) => (v != null && v > 0 ? fmt(v) : '—')
const ratio = (v: number | null) => (v != null && v > 0 ? pct(v) : '—')
const sumOrNull = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) : null)

function recordedValue(deal: Loan): number | null {
  if (typeof deal.propertyValue === 'number' && Number.isFinite(deal.propertyValue)) return deal.propertyValue
  return null
}

function recordedNoi(deal: Loan): number | null {
  if (typeof deal.noi === 'number' && Number.isFinite(deal.noi)) return deal.noi
  if (typeof deal.cashFlows?.prospectiveNOI === 'number' && Number.isFinite(deal.cashFlows.prospectiveNOI)) {
    return deal.cashFlows.prospectiveNOI
  }
  return null
}

/** An amount in $MM: live loans record dollars, the demo book declares $MM.
 *  The unit is never guessed from the size (an $800 NOI read as $800M). */
function scale(n: number, deal: Pick<Loan, 'amountUnit'>): number {
  return toMillions(n, amountUnitOf(deal))
}

type PostCloseStatus = 'On file' | 'Improving' | 'Declining' | 'Stable'

interface PostCloseValuation {
  dealId: string
  property: string
  location: string
  type: string
  origValue: number
  currentValue: number
  valueChange: number
  valueChangePct: number
  origNoi: number
  currentNoi: number
  origCapRate: number
  currentCapRate: number
  currentLtv: number
  origLtv: number
  lastValuationDate: string
  nextReview: string
  status: PostCloseStatus
}

function origLtvOf(deal: Loan): number {
  return typeof deal.ltv === 'number' && Number.isFinite(deal.ltv) ? deal.ltv : 0
}

/** Live path uses only recorded value/NOI. Demo may keep the legacy drift view. */
function generatePostCloseValuations(deals: Loan[], live?: boolean): PostCloseValuation[] {
  return deals.flatMap((d): PostCloseValuation[] => {
    const origValueRaw = recordedValue(d)
    const origNoiRaw = recordedNoi(d)
    if (live) {
      if (origValueRaw == null && origNoiRaw == null) return []
      const origValue = origValueRaw != null ? scale(origValueRaw, d) : 0
      const origNoi = origNoiRaw != null ? scale(origNoiRaw, d) : 0
      const loanAmount = scale(typeof d.loanAmount === 'number' ? d.loanAmount : 0, d)
      const currentLtv = origValue > 0 && loanAmount > 0 ? loanAmount / origValue : (typeof d.ltv === 'number' ? d.ltv : 0)
      return [{
        dealId: d.id,
        property: d.property,
        location: d.location,
        type: d.type,
        origValue,
        currentValue: origValue,
        valueChange: 0,
        valueChangePct: 0,
        origNoi,
        currentNoi: origNoi,
        origCapRate: origValue > 0 && origNoi > 0 ? origNoi / origValue : 0,
        currentCapRate: origValue > 0 && origNoi > 0 ? origNoi / origValue : 0,
        currentLtv,
        origLtv: origLtvOf(d),
        lastValuationDate: d.originated || '',
        nextReview: '',
        status: 'On file' as const,
      }]
    }
    const origValue = origValueRaw != null ? scale(origValueRaw, d) : scale(d.loanAmount / (d.ltv || 0.70), d)
    const origNoi = origNoiRaw != null ? scale(origNoiRaw, d) : 0
    const drift = (Math.random() - 0.45) * 0.12
    const currentValue = origValue * (1 + drift)
    const noiDrift = (Math.random() - 0.4) * 0.08
    const currentNoi = origNoi * (1 + noiDrift)
    const currentCapRate = currentValue > 0 ? currentNoi / currentValue : 0
    const currentLtv = currentValue > 0 ? scale(d.loanAmount, d) / currentValue : 0
    return [{
      dealId: d.id,
      property: d.property,
      location: d.location,
      type: d.type,
      origValue,
      currentValue,
      valueChange: currentValue - origValue,
      valueChangePct: origValue > 0 ? (currentValue - origValue) / origValue : 0,
      origNoi,
      currentNoi,
      origCapRate: origValue > 0 && origNoi > 0 ? origNoi / origValue : 0,
      currentCapRate,
      currentLtv,
      origLtv: origLtvOf(d),
      lastValuationDate: '2026-03-15',
      nextReview: '2026-06-15',
      status: drift > 0.03 ? 'Improving' as const : drift < -0.03 ? 'Declining' as const : 'Stable' as const,
    }]
  })
}

export default function PostCloseValuationPanel({ deals, live }: Props) {
  const valuations = generatePostCloseValuations(deals, live)
  // Totals and averages run over the loans that record the figure. Summing an
  // empty list printed "$0.0M · $0.0M · 0.0%" (current value in green) for
  // two funded loans with nothing recorded.
  const totalOrigValue = sumOrNull(valuations.map((v) => v.origValue).filter((x) => x > 0))
  const totalCurrentValue = sumOrNull(valuations.map((v) => v.currentValue).filter((x) => x > 0))
  const capRates = valuations.map((v) => v.currentCapRate).filter((x) => x > 0)
  const avgCapRate = capRates.length ? capRates.reduce((a, b) => a + b, 0) / capRates.length : null
  const declining = valuations.filter((v) => v.status === 'Declining').length

  return (
    <div className="space-y-3">
      {/* Valuation Type Badge */}
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">POST-CLOSE</span>
        <span className="text-[11px] text-stone-500">Ongoing portfolio valuation monitoring</span>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-4 gap-3">
        <div className="metric-cell">
          <div className="metric-cell-label flex items-center gap-1"><Building2 size={11} /> Orig. Portfolio Value</div>
          <div className="metric-cell-value">{money(totalOrigValue)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label flex items-center gap-1"><TrendingUp size={11} /> Current Portfolio Value</div>
          <div
            className={`metric-cell-value ${
              totalCurrentValue == null || totalOrigValue == null
                ? ''
                : totalCurrentValue >= totalOrigValue
                  ? 'text-emerald-400'
                  : 'text-red-400'
            }`}
          >
            {money(totalCurrentValue)}
          </div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Avg Cap Rate</div>
          <div className="metric-cell-value">{ratio(avgCapRate)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label flex items-center gap-1"><TrendingDown size={11} /> Declining Assets</div>
          <div className={`metric-cell-value ${live ? '' : declining > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{live ? '—' : declining}</div>
        </div>
      </div>

      {/* Valuation Table */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Post-Close Valuation Summary</span>
          <span className="text-[10px] font-mono text-stone-600">{valuations.length} assets</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Type</th>
                <th className="text-right">Orig. Value</th>
                <th className="text-right">Current Value</th>
                <th className="text-right">Change</th>
                <th className="text-right">Cap Rate</th>
                <th className="text-right">NOI (Curr)</th>
                <th className="text-right">LTV (Orig)</th>
                <th className="text-right">LTV (Curr)</th>
                <th className="text-center">Status</th>
                <th>Next Review</th>
              </tr>
            </thead>
            <tbody>
              {valuations.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-stone-500 text-[11px]">
                    {live ? 'No recorded property values or NOI on funded live loans.' : 'No post-close valuations.'}
                  </td>
                </tr>
              ) : null}
              {valuations.map((v) => (
                <tr key={v.dealId}>
                  <td className="font-medium text-stone-200 whitespace-nowrap">{v.property}</td>
                  <td className="text-stone-400">{v.type}</td>
                  <td className="text-right font-mono">{money(v.origValue)}</td>
                  <td className="text-right font-mono">{money(v.currentValue)}</td>
                  {live ? (
                    // No post-close revaluation is recorded on a live loan, so there is no change to show.
                    <td className="text-right font-mono text-stone-500">—</td>
                  ) : (
                    <td className={`text-right font-mono ${v.valueChangePct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      <span className="flex items-center justify-end gap-0.5">
                        {v.valueChangePct >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {v.valueChangePct >= 0 ? '+' : ''}{(v.valueChangePct * 100).toFixed(1)}%
                      </span>
                    </td>
                  )}
                  <td className="text-right font-mono">{ratio(v.currentCapRate)}</td>
                  <td className="text-right font-mono">{money(v.currentNoi)}</td>
                  <td className="text-right font-mono">{ratio(v.origLtv)}</td>
                  <td className={`text-right font-mono ${v.currentLtv > 0.75 ? 'text-red-400' : 'text-stone-200'}`}>
                    {ratio(v.currentLtv)}
                  </td>
                  <td className="text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${
                      v.status === 'Improving' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
                      v.status === 'Declining' ? 'text-red-400 bg-red-400/10 border-red-400/20' :
                      'text-stone-400 bg-stone-400/10 border-stone-400/20'
                    }`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="text-stone-400 text-[11px]">{v.nextReview || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
